/**
 * Plan-limit reached popup (DNKADM-0065) — the shared upgrade dialog the three v2
 * Add flows (locations / staff / resources) show when dink-api rejects a create
 * with `PLAN_LIMIT_REACHED` (409, DNKAPI-0139). One component, three call sites —
 * each Add dialog parses the rejection with `parsePlanLimit(err)` and renders this.
 *
 * It is purely informational: no record was created, so dismissing returns to the
 * Add dialog/list (the admin can delete an existing record to free a slot, contact
 * us to upgrade, or cancel). It never fetches — it words ONLY the
 * `axis`/`limit`/`current`/`plan` the rejection carried, all of which may be
 * absent/`null`:
 *  - `axis` → a natural noun (`locations`, `staff` → "staff members", `resources`);
 *    an unknown/empty axis degrades to a generic "items".
 *  - `current`/`limit` → an "(N of M)" count, shown only when both are present.
 *  - `plan` → an "on the {plan} plan" clause, omitted when `null`.
 *
 * The upgrade CTA is contact-to-upgrade copy (the rejection carries NO backend
 * contact details, and there is no in-app billing flow — Non-Goals); the
 * "delete one to add one" hint reflects that counts are of ACTIVE records.
 */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';
import { useId } from 'react';
import type { PlanLimitInfo } from '@/api/v2Errors';
import { scrubBrand } from '@/utils/whitelabel';

export interface PlanLimitDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** The parsed plan-limit payload, or `null` (closed — nothing to show). */
  info: PlanLimitInfo | null;
  /** Dismiss the popup (returns to the Add dialog/list — no record was created). */
  onClose: () => void;
}

/**
 * Map a plan-limit `axis` to a natural display noun. Unknown/empty axes degrade to
 * a generic "items" so a future axis still reads sensibly.
 */
function axisNoun(axis: string): string {
  switch (axis) {
    case 'locations':
      return 'locations';
    case 'staff':
      return 'staff members';
    case 'resources':
      return 'resources';
    default:
      return 'items';
  }
}

export function PlanLimitDialog({ open, info, onClose }: PlanLimitDialogProps) {
  const ids = useId();
  if (!open || info === null) return null;

  const noun = axisNoun(info.axis);
  // "(N of M)" only when both counts are present; the plan clause only when named.
  const count =
    info.current !== null && info.limit !== null ? ` (${info.current} of ${info.limit})` : '';
  const planClause = info.plan !== null ? ` on the ${scrubBrand(info.plan)} plan` : '';

  const titleId = `${ids}-title`;
  const bodyId = `${ids}-body`;

  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} aria-describedby={bodyId} maxWidth="xs" fullWidth>
      <DialogTitle id={titleId}>Plan limit reached</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
          <DialogContentText id={bodyId} sx={{ fontSize: 14 }}>
            You&rsquo;ve reached your plan&rsquo;s limit for {noun}
            {count}
            {planClause}.
          </DialogContentText>
          <DialogContentText sx={{ fontSize: 14 }}>
            Contact us to upgrade your subscription and add more {noun}. You can also delete an
            existing one to free up a slot, then add another.
          </DialogContentText>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
